/*
Copy of code from "@urql/exchange-graphcache/extras", with customization
for our schema with the nested relay pagination args.

The goal with this customization is to update our backend gradually to support the
standard relay pagination args, without having to update all of our frontend code at once.

Once all of our paginated fields support the standard relay pagination args, we can remove this file
and use the original `relayPagination` resolver from `@urql/exchange-graphcache/extras` instead.
*/

import { stringifyVariables } from "@urql/core";
import type {
  Cache,
  DataField,
  ResolveInfo,
  Resolver,
  Variables,
} from "@urql/exchange-graphcache";

type MergeMode = "inwards" | "outwards";

interface PaginationParams {
  mergeMode?: MergeMode;
  /** When pagination args (after, before, first, last) are nested inside a
   *  field argument object, set this to the key name. E.g. `"input"` when
   *  the query uses `field(input: { after: ..., first: ... })`. */
  inputPath?: string;
}

interface PageInfoData {
  __typename: string;
  endCursor: string | null;
  startCursor: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface PageData {
  __typename: string;
  edges: readonly string[];
  nodes: readonly string[];
  pageInfo: PageInfoData;
}

interface PaginationArgs {
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  [key: string]: unknown;
}

const defaultPageInfo: PageInfoData = {
  __typename: "PageInfo",
  endCursor: null,
  startCursor: null,
  hasNextPage: false,
  hasPreviousPage: false,
};

const ensureKey = (x: DataField | undefined): string | null =>
  typeof x === "string" ? x : null;

const concatEdges = (
  cache: Cache,
  leftEdges: readonly string[],
  rightEdges: readonly string[],
): string[] => {
  const ids = new Set<string>();
  for (let i = 0, l = leftEdges.length; i < l; i++) {
    const edge = leftEdges[i];
    const node = cache.resolve(edge, "node");
    if (typeof node === "string") ids.add(node);
  }
  const newEdges = leftEdges.slice();
  for (let i = 0, l = rightEdges.length; i < l; i++) {
    const edge = rightEdges[i];
    const node = cache.resolve(edge, "node");
    if (typeof node === "string" && !ids.has(node)) {
      ids.add(node);
      newEdges.push(edge);
    }
  }
  return newEdges;
};

const concatNodes = (
  leftNodes: readonly string[],
  rightNodes: readonly string[],
): string[] => {
  const ids = new Set<string>();
  for (let i = 0, l = leftNodes.length; i < l; i++) {
    const node = leftNodes[i];
    if (typeof node === "string") ids.add(node);
  }
  const newNodes = leftNodes.slice();
  for (let i = 0, l = rightNodes.length; i < l; i++) {
    const node = rightNodes[i];
    if (typeof node === "string" && !ids.has(node)) {
      ids.add(node);
      newNodes.push(node);
    }
  }
  return newNodes;
};

const isPaginationKey = (key: string): boolean =>
  key === "first" || key === "last" || key === "after" || key === "before";

const getPaginationArgs = (
  args: Variables,
  inputPath: string | undefined,
): PaginationArgs => {
  if (!inputPath) return args as PaginationArgs;
  const nested = args[inputPath];
  return typeof nested === "object" && nested !== null
    ? (nested as PaginationArgs)
    : {};
};

const compareArgs = (
  fieldArgs: Variables,
  connectionArgs: Variables,
  inputPath: string | undefined,
): boolean => {
  for (const key in connectionArgs) {
    if (!inputPath && isPaginationKey(key)) continue;
    if (!(key in fieldArgs)) return false;

    const argA = fieldArgs[key];
    const argB = connectionArgs[key];

    if (key === inputPath) {
      // Compare the nested input objects ignoring pagination keys
      if (typeof argA !== "object" || typeof argB !== "object") {
        if (argA !== argB) return false;
        continue;
      }
      const objA = argA as Variables;
      const objB = argB as Variables;
      for (const nestedKey in objB) {
        if (isPaginationKey(nestedKey)) continue;
        if (!(nestedKey in objA)) {
          // Treat null/undefined as "not provided" — compatible with field args missing it
          if (objB[nestedKey] == null) continue;
          return false;
        }
        const nA = objA[nestedKey];
        const nB = objB[nestedKey];
        if (
          typeof nA !== typeof nB || typeof nA !== "object"
            ? nA !== nB
            : stringifyVariables(nA) !== stringifyVariables(nB)
        ) {
          return false;
        }
      }
      for (const nestedKey in objA) {
        if (isPaginationKey(nestedKey)) continue;
        if (!(nestedKey in objB)) {
          // Treat null/undefined as "not provided" — compatible with stored args missing it
          if (objA[nestedKey] == null) continue;
          return false;
        }
      }
      continue;
    }

    if (
      typeof argA !== typeof argB || typeof argA !== "object"
        ? argA !== argB
        : stringifyVariables(argA) !== stringifyVariables(argB)
    ) {
      return false;
    }
  }
  for (const key in fieldArgs) {
    if (!inputPath && isPaginationKey(key)) continue;
    if (!(key in connectionArgs)) return false;
  }
  return true;
};

const getPage = (
  cache: Cache,
  entityKey: string,
  fieldKey: string,
): PageData | null => {
  const link = ensureKey(cache.resolve(entityKey, fieldKey));
  if (!link) return null;
  const typename = cache.resolve(link, "__typename");
  const edges = (cache.resolve(link, "edges") || []) as readonly string[];
  const nodes = (cache.resolve(link, "nodes") || []) as readonly string[];
  if (typeof typename !== "string") {
    return null;
  }
  const page: PageData = {
    __typename: typename,
    edges,
    nodes,
    pageInfo: defaultPageInfo,
  };
  const pageInfoKey = cache.resolve(link, "pageInfo");
  if (typeof pageInfoKey === "string") {
    const pageInfoType = ensureKey(cache.resolve(pageInfoKey, "__typename"));
    const endCursor = ensureKey(cache.resolve(pageInfoKey, "endCursor"));
    const startCursor = ensureKey(cache.resolve(pageInfoKey, "startCursor"));
    const hasNextPage = cache.resolve(pageInfoKey, "hasNextPage");
    const hasPreviousPage = cache.resolve(pageInfoKey, "hasPreviousPage");
    const pageInfo: PageInfoData = (page.pageInfo = {
      __typename: typeof pageInfoType === "string" ? pageInfoType : "PageInfo",
      hasNextPage: typeof hasNextPage === "boolean" ? hasNextPage : !!endCursor,
      hasPreviousPage:
        typeof hasPreviousPage === "boolean" ? hasPreviousPage : !!startCursor,
      endCursor,
      startCursor,
    });
    if (pageInfo.endCursor === null) {
      const edge = edges[edges.length - 1];
      if (edge) {
        const cursor = cache.resolve(edge, "cursor");
        pageInfo.endCursor = ensureKey(cursor);
      }
    }
    if (pageInfo.startCursor === null) {
      const edge = edges[0];
      if (edge) {
        const cursor = cache.resolve(edge, "cursor");
        pageInfo.startCursor = ensureKey(cursor);
      }
    }
  }
  return page;
};

/** Creates a {@link Resolver} that combines pages that comply to the Relay pagination spec.
 *
 * @param params - A {@link PaginationParams} configuration object.
 * @returns the created Relay pagination {@link Resolver}.
 *
 * @remarks
 * `relayPagination` is a factory that creates a {@link Resolver} that can combine
 * multiple pages on a field that complies to the Relay pagination spec into a single,
 * combined list for infinite scrolling.
 *
 * This resolver will only work on fields that return a `Connection` GraphQL object
 * type, according to the Relay pagination spec.
 *
 * @see {@link https://urql.dev/goto/docs/graphcache/local-resolvers#relay-pagination} for more information.
 * @see {@link https://urql.dev/goto/docs/basics/ui-patterns/#infinite-scrolling} for an alternate approach.
 */
export const relayPagination = (params: PaginationParams = {}): Resolver => {
  const mergeMode = params.mergeMode || "inwards";
  const inputPath = params.inputPath || undefined;
  return (
    _parent: Record<string, unknown>,
    fieldArgs: Variables,
    cache: Cache,
    info: ResolveInfo,
  ) => {
    const { parentKey: entityKey, fieldName } = info;
    const allFields = cache.inspectFields(entityKey);
    const fieldInfos = allFields.filter((f) => f.fieldName === fieldName);
    const size = fieldInfos.length;
    if (size === 0) {
      return undefined;
    }
    let typename: string | null = null;
    let startEdges: string[] = [];
    let endEdges: string[] = [];
    let startNodes: string[] = [];
    let endNodes: string[] = [];
    let pageInfo: PageInfoData = {
      ...defaultPageInfo,
    };
    for (let i = 0; i < size; i++) {
      const { fieldKey, arguments: args } = fieldInfos[i];
      if (args === null || !compareArgs(fieldArgs, args, inputPath)) {
        continue;
      }
      const page = getPage(cache, entityKey, fieldKey);
      if (page === null) {
        continue;
      }
      if (page.nodes.length === 0 && page.edges.length === 0 && typename) {
        continue;
      }
      const pArgs = getPaginationArgs(args, inputPath);
      if (
        mergeMode === "inwards" &&
        typeof pArgs.last === "number" &&
        typeof pArgs.first === "number"
      ) {
        const firstEdges = page.edges.slice(0, pArgs.first + 1);
        const lastEdges = page.edges.slice(-pArgs.last);
        const firstNodes = page.nodes.slice(0, pArgs.first + 1);
        const lastNodes = page.nodes.slice(-pArgs.last);
        startEdges = concatEdges(cache, startEdges, firstEdges);
        endEdges = concatEdges(cache, lastEdges, endEdges);
        startNodes = concatNodes(startNodes, firstNodes);
        endNodes = concatNodes(lastNodes, endNodes);
        pageInfo = page.pageInfo;
      } else if (pArgs.after) {
        startEdges = concatEdges(cache, startEdges, page.edges);
        startNodes = concatNodes(startNodes, page.nodes);
        pageInfo.endCursor = page.pageInfo.endCursor;
        pageInfo.hasNextPage = page.pageInfo.hasNextPage;
      } else if (pArgs.before) {
        endEdges = concatEdges(cache, page.edges, endEdges);
        endNodes = concatNodes(page.nodes, endNodes);
        pageInfo.startCursor = page.pageInfo.startCursor;
        pageInfo.hasPreviousPage = page.pageInfo.hasPreviousPage;
      } else if (typeof pArgs.last === "number") {
        endEdges = concatEdges(cache, page.edges, endEdges);
        endNodes = concatNodes(page.nodes, endNodes);
        pageInfo = page.pageInfo;
      } else {
        startEdges = concatEdges(cache, startEdges, page.edges);
        startNodes = concatNodes(startNodes, page.nodes);
        pageInfo = page.pageInfo;
      }
      if (page.pageInfo.__typename !== pageInfo.__typename)
        pageInfo.__typename = page.pageInfo.__typename;
      if (typename !== page.__typename) typename = page.__typename;
    }
    if (typeof typename !== "string") {
      return undefined;
    }
    const hasCurrentPage = !!ensureKey(
      cache.resolve(entityKey, fieldName, fieldArgs),
    );
    if (!hasCurrentPage) {
      // @ts-ignore - code from original source
      if (!info.store.schema) {
        return undefined;
      } else {
        info.partial = true;
      }
    }
    return {
      __typename: typename,
      edges:
        mergeMode === "inwards"
          ? concatEdges(cache, startEdges, endEdges)
          : concatEdges(cache, endEdges, startEdges),
      nodes:
        mergeMode === "inwards"
          ? concatNodes(startNodes, endNodes)
          : concatNodes(endNodes, startNodes),
      pageInfo: {
        __typename: pageInfo.__typename,
        endCursor: pageInfo.endCursor,
        startCursor: pageInfo.startCursor,
        hasNextPage: pageInfo.hasNextPage,
        hasPreviousPage: pageInfo.hasPreviousPage,
      },
    };
  };
};
